/**
 * useFloorPlan3DExtraSync — keeps the chargeable 3D-upgrade extra in step with
 * the configurator's render-type toggle.
 *
 * 3D floor plans cost more than 2D, and the catalogue prices that difference as
 * a separate extra product. Picking 3D in the configurator is therefore not a
 * free styling choice — it has to attach the extra, or the client is shown 3D
 * and billed for 2D.
 *
 * It is billed PER PLAN: a line covering three uploaded plans buys three 3D
 * conversions. `planCount` is the line's uploaded plans, falling back to the
 * line quantity for a floor-plan product that takes no uploads (the plans come
 * from the shoot). Never zero — a 3D line always converts at least one plan.
 *
 * The toggle OWNS the extra: the drawer withholds it from its manual extras
 * list (`ProductDrawer`), so nothing else can tick it and disagree.
 *
 * The configuration is shared by every floor-plan line, but 3D is decided per
 * line (`useFloorPlanProductFacts`), so this runs for all of them, not only the
 * one on screen: a line that cannot bill 3D is kept at 2D, and every 3D line
 * carries its extra whether or not it was ever opened.
 */

import { useEffect, useMemo } from 'react'

import { logger } from 'utils/logging'

import type { CartMutationHandler } from '../../adapters/bridge'
import { usePfStore } from '../../store'
import { makeConfigKey } from '../../store/slices/productConfigSlice'
import { asTwoDimensional, FLOOR_PLAN_CONFIG_DEFAULT, type FloorPlanConfigDraftDTO } from './floorPlanConfig.dto'
import { FloorPlanRenderType } from 'constants/floorPlan'
import { useFloorPlanLines } from './useFloorPlanLineIds'

/** How many plans one line covers: what was uploaded for it, else its quantity. */
export const planCountForLine = (
  uploadedCount: number,
  quantity: number
): number => Math.max(1, uploadedCount || quantity)

export function useFloorPlan3DExtraSync(onCartMutation?: CartMutationHandler): void {
  const lines = useFloorPlanLines()
  const configs = usePfStore((s) => s.productConfigs)
  const attachments = usePfStore((s) => s.attachments)
  const items = usePfStore((s) => s.items)

  const states = useMemo(() => lines.map((line) => {
    const config = configs[makeConfigKey(line.id, 'floor_plan_config')] as FloorPlanConfigDraftDTO | undefined
    const quantity = items.find((i) => i.id === line.id)?.quantity ?? 1
    return {
      line,
      config,
      is3D: config?.currentFloorPlanRenderType === FloorPlanRenderType.RENDER_3D,
      planCount: planCountForLine(attachments[line.id]?.length ?? 0, quantity),
    }
  }), [lines, configs, attachments, items])

  // A 3D configuration shared from another line, or resumed from a draft, is taken back to 2D on a
  // line that cannot bill it, so the plan is never delivered as 3D and charged as 2D.
  useEffect(() => {
    for (const { line, config, is3D } of states) {
      if (!is3D || !config || !line.isCatalogueResolved || line.is3DAllowed) continue
      if (!line.threeDExtraId) logger.warn('PF revamp floor plan: 3D configured on a line that offers no 3D extra, kept at 2D', { cartItemId: line.id })
      usePfStore.getState().patchConfig(line.id, 'floor_plan_config', asTwoDimensional({ ...FLOOR_PLAN_CONFIG_DEFAULT, ...config }))
    }
  }, [states])

  useEffect(() => {
    if (!onCartMutation) return
    for (const { line, is3D, planCount } of states) {
      if (!line.threeDExtraId) continue
      if (is3D && line.is3DAllowed) {
        onCartMutation({ type: 'select-extra', productId: line.threeDExtraId, parentCartItemId: line.id, count: planCount })
        continue
      }
      onCartMutation({ type: 'unselect-extra', productId: line.threeDExtraId, parentCartItemId: line.id })
    }
  }, [states, onCartMutation])
}
